import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db, requestNotificationPermission } from '@/lib/firebase';
import { useAuth } from '@/context/AuthContext';
import {
    collection,
    addDoc,
    query,
    where,
    orderBy,
    onSnapshot,
    Timestamp,
    deleteDoc,
    doc,
    setDoc
} from 'firebase/firestore';
import { improveWriting } from '@/lib/gemini';
import { speechToText } from '@/lib/speech';
import { sendChatMessage } from '@/lib/chatService';
import { getCurrentUTCDate, toUTCFromLocalInput, toLocalDateString, toLocalTimeString, toLocalInputString, isSameDay, fromFirestoreTimestamp } from '@/lib/dateUtils';
import {
    Calendar,
    MessageCircle,
    BookOpen,
    Settings,
    LogOut,
    Send,
    Download,
    Sun,
    Moon,
    Monitor,
    Bell,
    Sparkles,
    Loader2,
    Mic,
    MicOff,
    Wand2,
    Undo2,
    X,
    Trash2,
    Languages,
    User,
    Palette,
    Bot,
    FileJson,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    AlertTriangle
} from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

// Single-glyph representation per voice-input language (shown on the compact trigger)
const VOICE_LANGUAGE_GLYPHS = {
    'en-US': 'A',
    'hi-IN': 'अ',
    'ta-IN': 'த',
    'te-IN': 'అ',
    'ml-IN': 'അ',
    'kn-IN': 'ಅ',
    'bn-IN': 'অ',
    'mr-IN': 'अ',
    'gu-IN': 'અ',
};

const VOICE_LANGUAGE_LABELS = {
    'en-US': 'English',
    'hi-IN': 'Hindi',
    'ta-IN': 'Tamil',
    'te-IN': 'Telugu',
    'ml-IN': 'Malayalam',
    'kn-IN': 'Kannada',
    'bn-IN': 'Bengali',
    'mr-IN': 'Marathi',
    'gu-IN': 'Gujarati',
};

const AVATAR_STYLES = [
    'avataaars',
    'micah',
    'notionists',
    'open-peeps',
    'big-smile',
    'fun-emoji',
    'bottts',
    'pixel-art'
];

function hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash |= 0;
    }
    return Math.abs(hash);
}

function getAvatarUrl(seed) {
    if (!seed) return null;
    const style = AVATAR_STYLES[hashString(seed) % AVATAR_STYLES.length];
    return `https://api.dicebear.com/9.x/${style}/svg?seed=${encodeURIComponent(seed)}`;
}

function Dashboard() {
    const { currentUser: user, logout } = useAuth();
    const [activeTab, setActiveTab] = useState('journal');
    const [entries, setEntries] = useState([]);
    const [newEntry, setNewEntry] = useState('');
    const [entryDate, setEntryDate] = useState(() => toLocalInputString());
    const [saving, setSaving] = useState(false);
    const [improving, setImproving] = useState(false);
    const [originalEntry, setOriginalEntry] = useState('');
    const [theme, setTheme] = useState('system');
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
    const [entryToDelete, setEntryToDelete] = useState(null);
    const [isListening, setIsListening] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [voiceSupported, setVoiceSupported] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('en-US');
    const [assistantName, setAssistantName] = useState('Juno');
    const [assistantNameDraft, setAssistantNameDraft] = useState('Juno');
    const [avatarError, setAvatarError] = useState(false);
    const [greeting] = useState(() => {
        const greetings = ['whatsup', 'anything to say', "what's going on", "what's happening", "how it's going"];
        return greetings[Math.floor(Math.random() * greetings.length)];
    });
    const avatarUrl = useMemo(() => getAvatarUrl(user?.uid || user?.email || ''), [user?.uid, user?.email]);
    const chatEndRef = useRef(null);
    const recognitionRef = useRef(null);
    const processedIndexRef = useRef(0);
    const voiceModeRef = useRef(null);
    const cloudSessionRef = useRef(null);
    const autoStopTimerRef = useRef(null);

    useEffect(() => {
        if (!user) return;

        // New collection path: users/{uid}/entries
        const entriesRef = collection(db, 'users', user.uid, 'entries');
        const q = query(
            entriesRef,
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const entriesData = snapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    createdAt: fromFirestoreTimestamp(data.createdAt),
                    // Map old field names to new for backwards compatibility
                    text: data.text || data.content || '',
                };
            });
            setEntries(entriesData);
        });

        return () => unsubscribe();
    }, [user]);

    useEffect(() => {
        setAvatarError(false);
    }, [user?.uid, user?.email]);

    useEffect(() => {
        if (!user) return;

        const settingsRef = doc(db, 'settings', user.uid);
        const unsubscribe = onSnapshot(settingsRef, (snapshot) => {
            const data = snapshot.data();
            const name = (data?.assistantName || '').trim();
            const resolvedName = name || 'Juno';
            setAssistantName(resolvedName);
            setAssistantNameDraft(resolvedName);
        });

        return () => unsubscribe();
    }, [user]);

    useEffect(() => {
        const savedTheme = localStorage.getItem('amica-theme') || 'system';
        setTheme(savedTheme);
        applyTheme(savedTheme);

        // Load saved language preference
        const savedLanguage = localStorage.getItem('amica-voice-language') || 'en-US';
        setSelectedLanguage(savedLanguage);

        const hasGetUserMedia = !!(navigator?.mediaDevices?.getUserMedia);
        const hasSpeechRecognition = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
        setVoiceSupported(hasGetUserMedia || hasSpeechRecognition);

        if (hasSpeechRecognition) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = savedLanguage;

            recognitionRef.current.onresult = (event) => {
                let finalTranscript = '';

                const startIndex = Math.max(event.resultIndex, processedIndexRef.current);

                for (let i = startIndex; i < event.results.length; i++) {
                    if (event.results[i].isFinal) {
                        const transcript = event.results[i][0].transcript;
                        finalTranscript += transcript + ' ';
                        processedIndexRef.current = i + 1;
                    }
                }

                if (finalTranscript) {
                    setNewEntry((prev) => prev + finalTranscript);
                }
            };

            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
                if (event.error === 'not-allowed') {
                    toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
                } else if (event.error === 'no-speech') {
                    toast.error('No speech detected. Please try again.');
                } else {
                    toast.error('Voice input error. Please try again.');
                }
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            if (cloudSessionRef.current?.cleanup) {
                cloudSessionRef.current.cleanup();
            }
            if (autoStopTimerRef.current) {
                clearTimeout(autoStopTimerRef.current);
            }
        };
    }, []);

    const displayAssistantName = assistantName?.trim() || 'Juno';

    const arrayBufferToBase64 = (buffer) => {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        const chunkSize = 0x8000;
        for (let i = 0; i < bytes.length; i += chunkSize) {
            binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
        }
        return btoa(binary);
    };

    const encodeWav16Mono = ({ buffers, sampleRate }) => {
        const length = buffers.reduce((sum, b) => sum + b.length, 0);
        const pcm = new Int16Array(length);

        let offset = 0;
        for (const buffer of buffers) {
            for (let i = 0; i < buffer.length; i++) {
                const s = Math.max(-1, Math.min(1, buffer[i]));
                pcm[offset++] = s < 0 ? s * 0x8000 : s * 0x7fff;
            }
        }

        const wavBuffer = new ArrayBuffer(44 + pcm.length * 2);
        const view = new DataView(wavBuffer);
        let pos = 0;

        const writeString = (str) => {
            for (let i = 0; i < str.length; i++) {
                view.setUint8(pos++, str.charCodeAt(i));
            }
        };

        writeString('RIFF');
        view.setUint32(pos, 36 + pcm.length * 2, true); pos += 4;
        writeString('WAVE');
        writeString('fmt ');
        view.setUint32(pos, 16, true); pos += 4;
        view.setUint16(pos, 1, true); pos += 2;
        view.setUint16(pos, 1, true); pos += 2;
        view.setUint32(pos, sampleRate, true); pos += 4;
        view.setUint32(pos, sampleRate * 2, true); pos += 4;
        view.setUint16(pos, 2, true); pos += 2;
        view.setUint16(pos, 16, true); pos += 2;
        writeString('data');
        view.setUint32(pos, pcm.length * 2, true); pos += 4;

        for (let i = 0; i < pcm.length; i++, pos += 2) {
            view.setInt16(pos, pcm[i], true);
        }

        return wavBuffer;
    };

    const startCloudRecording = async () => {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        const canUseMediaRecorder = typeof window.MediaRecorder !== 'undefined' && typeof window.MediaRecorder.isTypeSupported === 'function';
        const preferredTypes = [
            'audio/webm;codecs=opus',
            'audio/ogg;codecs=opus',
            'audio/webm',
            'audio/ogg',
        ];
        const selectedType = canUseMediaRecorder ? preferredTypes.find((t) => window.MediaRecorder.isTypeSupported(t)) : null;

        if (selectedType) {
            const recorder = new MediaRecorder(stream, { mimeType: selectedType });
            const chunks = [];

            recorder.addEventListener('dataavailable', (e) => {
                if (e.data && e.data.size > 0) chunks.push(e.data);
            });

            const stopped = new Promise((resolve) => recorder.addEventListener('stop', resolve, { once: true }));
            recorder.start();

            const cleanup = () => {
                if (recorder.state !== 'inactive') recorder.stop();
                stream.getTracks().forEach((t) => t.stop());
            };

            return {
                cleanup,
                stop: async () => {
                    try {
                        if (recorder.state !== 'inactive') recorder.stop();
                        await stopped;
                        const blob = new Blob(chunks, { type: selectedType });
                        const buffer = await blob.arrayBuffer();
                        const audioBase64 = arrayBufferToBase64(buffer);
                        const encoding = selectedType.includes('ogg') ? 'OGG_OPUS' : 'WEBM_OPUS';
                        return { audioBase64, encoding };
                    } finally {
                        stream.getTracks().forEach((t) => t.stop());
                    }
                },
            };
        }

        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        const buffers = [];

        processor.onaudioprocess = (e) => {
            const input = e.inputBuffer.getChannelData(0);
            buffers.push(new Float32Array(input));
            const out = e.outputBuffer.getChannelData(0);
            out.fill(0);
        };

        source.connect(processor);
        processor.connect(audioContext.destination);

        const cleanup = () => {
            try { processor.disconnect(); } catch { }
            try { source.disconnect(); } catch { }
            stream.getTracks().forEach((t) => t.stop());
            audioContext.close();
        };

        return {
            cleanup,
            stop: async () => {
                cleanup();
                const sampleRate = Math.round(audioContext.sampleRate);
                const wavBuffer = encodeWav16Mono({ buffers, sampleRate });
                const audioBase64 = arrayBufferToBase64(wavBuffer);
                return { audioBase64, encoding: 'LINEAR16', sampleRateHertz: sampleRate };
            },
        };
    };

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatMessages]);

    const applyTheme = (newTheme) => {
        if (newTheme === 'system') {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            document.documentElement.classList.toggle('dark', prefersDark);
        } else {
            document.documentElement.classList.toggle('dark', newTheme === 'dark');
        }
    };

    const handleThemeChange = (newTheme) => {
        setTheme(newTheme);
        localStorage.setItem('amica-theme', newTheme);
        applyTheme(newTheme);
    };

    const handleLanguageChange = (newLanguage) => {
        setSelectedLanguage(newLanguage);
        localStorage.setItem('amica-voice-language', newLanguage);

        // Update Web Speech API language if available
        if (recognitionRef.current) {
            recognitionRef.current.lang = newLanguage;
        }

        toast.success(`Language changed to ${getLanguageName(newLanguage)}`);
    };

    const handleAssistantNameSave = async () => {
        if (!user) return;
        const trimmed = assistantNameDraft.trim();
        if (!trimmed) {
            toast.error('Assistant name cannot be empty');
            return;
        }

        try {
            const settingsRef = doc(db, 'settings', user.uid);
            await setDoc(settingsRef, { assistantName: trimmed }, { merge: true });
            setAssistantName(trimmed);
            toast.success('Assistant name updated');
        } catch (error) {
            console.error('Failed to update assistant name:', error);
            toast.error('Failed to update assistant name');
        }
    };

    const getLanguageName = (code) => {
        const languages = {
            'en-US': 'English',
            'hi-IN': 'Hindi',
            'ta-IN': 'Tamil',
            'te-IN': 'Telugu',
            'ml-IN': 'Malayalam',
            'kn-IN': 'Kannada',
            'bn-IN': 'Bengali',
            'mr-IN': 'Marathi',
            'gu-IN': 'Gujarati',
        };
        return languages[code] || code;
    };

    const getAlternativeLanguages = (primaryLanguage) => {
        // All supported languages
        const allLanguages = ['en-US', 'hi-IN', 'ta-IN', 'te-IN', 'ml-IN', 'kn-IN', 'bn-IN', 'mr-IN', 'gu-IN'];

        // Smart ordering: prioritize related languages
        const languageGroups = {
            'ta-IN': ['te-IN', 'ml-IN', 'kn-IN', 'hi-IN', 'en-US'], // Dravidian languages first
            'te-IN': ['ta-IN', 'kn-IN', 'ml-IN', 'hi-IN', 'en-US'],
            'ml-IN': ['ta-IN', 'kn-IN', 'te-IN', 'hi-IN', 'en-US'],
            'kn-IN': ['ta-IN', 'te-IN', 'ml-IN', 'hi-IN', 'en-US'],
            'hi-IN': ['mr-IN', 'gu-IN', 'bn-IN', 'en-US', 'ta-IN'], // Indo-Aryan languages first
            'mr-IN': ['hi-IN', 'gu-IN', 'bn-IN', 'en-US', 'ta-IN'],
            'gu-IN': ['hi-IN', 'mr-IN', 'bn-IN', 'en-US', 'ta-IN'],
            'bn-IN': ['hi-IN', 'mr-IN', 'gu-IN', 'en-US', 'ta-IN'],
            'en-US': ['hi-IN', 'ta-IN', 'te-IN', 'ml-IN', 'kn-IN'], // Common Indian languages
        };

        // Get the prioritized alternatives for the primary language
        const prioritized = languageGroups[primaryLanguage] || allLanguages.filter(l => l !== primaryLanguage);

        // Return alternatives excluding the primary language
        return prioritized.filter(lang => lang !== primaryLanguage).slice(0, 8);
    };

    const handleSignOut = async () => {
        try {
            await logout();
            setEntries([]);
            setChatMessages([]);
            toast.success('Signed out successfully');
        } catch (error) {
            console.error('Sign out error:', error);
        }
    };

    const handleImproveWriting = async () => {
        if (!newEntry.trim()) return;

        setImproving(true);
        try {
            // Save original text for undo
            setOriginalEntry(newEntry);
            
            // Improve the writing
            const improvedText = await improveWriting(newEntry);
            setNewEntry(improvedText);
            
            toast.success('Writing improved! ✨', {
                description: 'Click undo if you want to revert'
            });
        } catch (error) {
            console.error('Error improving writing:', error);
            toast.error('Failed to improve writing');
        } finally {
            setImproving(false);
        }
    };

    const handleUndoImprovement = () => {
        if (originalEntry) {
            setNewEntry(originalEntry);
            setOriginalEntry('');
            toast.success('Reverted to original text');
        }
    };

    const handleDeleteEntry = (entry) => {
        setEntryToDelete(entry);
    };

    const confirmDelete = async () => {
        if (!entryToDelete) return;

        try {
            await deleteDoc(doc(db, 'users', user.uid, 'entries', entryToDelete.id));
            toast.success('Entry deleted');
            setEntryToDelete(null);
        } catch (error) {
            console.error('Error deleting entry:', error);
            toast.error('Failed to delete entry');
        }
    };

    const handleSaveEntry = async () => {
        if (!newEntry.trim() || !user) return;

        // Client-side limit to match backend protection
        if (newEntry.length > 8000) {
            toast.error('Entry is too long. Please keep it under 8000 characters.');
            return;
        }

        setSaving(true);

        try {
            // Save entry with minimal fields - Cloud Functions will handle AI analysis
            const entryData = {
                text: newEntry,
                createdAt: Timestamp.fromDate(toUTCFromLocalInput(entryDate)),
                updatedAt: Timestamp.now(),
                ai_status: {
                    analyzed: false,
                    embedded: false
                }
            };

            await addDoc(collection(db, 'users', user.uid, 'entries'), entryData);

            setNewEntry('');
            setOriginalEntry(''); // Clear original entry on save
            setEntryDate(toLocalInputString());
            toast.success('Entry saved! AI analysis in progress...', {
                icon: '✨'
            });
        } catch (error) {
            console.error('Error saving entry:', error);
            toast.error('Failed to save entry');
        } finally {
            setSaving(false);
        }
    };

    const handleChatSend = async () => {
        if (!chatInput.trim() || chatLoading) return;

        const userMessage = chatInput.trim();

        if (userMessage.length > 2000) {
            toast.error('Message is too long. Please keep chat messages under 2000 characters.');
            return;
        }
        setChatInput('');
        setChatMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
        setChatLoading(true);

        try {
            // Call Cloud Function - it will handle RAG retrieval
            const { reply } = await sendChatMessage(userMessage, displayAssistantName);
            setChatMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
        } catch (error) {
            console.error('Chat error:', error);
            toast.error(error.message || 'Failed to send message');
            // Remove the user message if the request failed
            setChatMessages((prev) => prev.slice(0, -1));
        } finally {
            setChatLoading(false);
        }
    };

    const handleExportData = () => {
        const exportData = {
            exportDate: getCurrentUTCDate().toISOString(),
            exportTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            entries: entries.map((entry) => ({
                content: entry.text || entry.content,
                date: entry.createdAt,
                dateLocal: toLocalDateString(entry.createdAt),
                mood: entry.moodLabel,
                emoji: entry.emoji,
                aiResponse: entry.aiResponse
            }))
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
            type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `amica-journal-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Journal exported successfully');
    };

    const handleRequestNotifications = async () => {
        const token = await requestNotificationPermission();
        if (token) {
            toast.success('Notifications enabled!');
        } else {
            toast.error('Failed to enable notifications');
        }
    };

    const toggleVoiceInput = async () => {
        if (!voiceSupported) {
            toast.error('Voice input is not supported in your browser.');
            return;
        }

        // Prevent race condition on rapid toggle
        if (isListening || isTranscribing) {
            if (!isListening) return; // Already processing
        }

        if (isListening) {
            setIsListening(false);
            if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current);

            if (voiceModeRef.current === 'cloud' && cloudSessionRef.current?.stop) {
                const session = cloudSessionRef.current;
                cloudSessionRef.current = null;
                voiceModeRef.current = null;

                setIsTranscribing(true);
                toast.success('Processing voice...');
                try {
                    const payload = await session.stop();
                    const result = await speechToText({
                        ...payload,
                        languageCode: selectedLanguage,
                        alternativeLanguageCodes: getAlternativeLanguages(selectedLanguage),
                    });
                    const transcript = (result?.transcript || '').trim();
                    if (transcript) {
                        setNewEntry((prev) => (prev ? `${prev.trimEnd()} ${transcript} ` : `${transcript} `));
                    } else {
                        toast.error('No speech detected. Please try again.');
                    }
                } catch (error) {
                    console.error('Speech-to-text failed:', error);
                    const errorMessage = error?.message || 'Voice input failed. Please try again.';
                    toast.error(errorMessage);
                } finally {
                    setIsTranscribing(false);
                }
                return;
            }

            if (recognitionRef.current) {
                recognitionRef.current.stop();
                toast.success('Voice input stopped');
            }
        } else {
            const canUseCloud = !!(navigator?.mediaDevices?.getUserMedia);
            if (canUseCloud) {
                try {
                    voiceModeRef.current = 'cloud';
                    const session = await startCloudRecording();
                    cloudSessionRef.current = session;
                    setIsListening(true);
                    toast.success('Voice input started. Tap again to stop.');
                    autoStopTimerRef.current = setTimeout(() => {
                        toggleVoiceInput();
                    }, 60000);
                    return;
                } catch (error) {
                    console.error('Error starting audio recording:', error);
                    voiceModeRef.current = null;
                    cloudSessionRef.current = null;
                    
                    // Provide specific error messages
                    if (error.name === 'NotAllowedError') {
                        toast.error('Microphone access denied. Please allow microphone in browser settings.');
                    } else if (error.name === 'NotFoundError') {
                        toast.error('No microphone found. Please connect a microphone.');
                    } else {
                        toast.error('Failed to start voice input');
                    }
                }
            }

            if (!recognitionRef.current) {
                toast.error('Voice input is not supported in your browser.');
                return;
            }

            try {
                voiceModeRef.current = 'browser';
                processedIndexRef.current = 0;
                recognitionRef.current.start();
                setIsListening(true);
                toast.success('Voice input started. Start speaking...');
            } catch (error) {
                console.error('Error starting voice recognition:', error);
                toast.error('Failed to start voice input');
                voiceModeRef.current = null;
            }
        }
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        return { daysInMonth, startingDayOfWeek };
    };

    const getEntriesForDate = (date) => {
        return entries.filter((entry) => {
            return isSameDay(entry.createdAt, date);
        });
    };

    const groupEntriesByDate = (entriesToGroup) => {
        const grouped = {};
        entriesToGroup.forEach((entry) => {
            const dateKey = toLocalDateString(entry.createdAt);
            if (!grouped[dateKey]) {
                grouped[dateKey] = [];
            }
            grouped[dateKey].push(entry);
        });
        return grouped;
    };

    const filteredEntries = selectedDate
        ? getEntriesForDate(selectedDate)
        : entries;

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-slate-100 dark:from-slate-900 dark:via-indigo-950 dark:to-slate-900 pb-20">

            {/* Header */}
            <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 dark:bg-slate-900/80 border-b border-slate-200/50 dark:border-slate-700/50">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                            Diary AI
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {user && avatarUrl && !avatarError ? (
                            <img
                                src={avatarUrl}
                                alt={user.displayName || 'User'}
                                onError={() => setAvatarError(true)}
                                className="w-8 h-8 rounded-full border-2 border-indigo-500"
                            />
                        ) : (
                            <div className="w-8 h-8 rounded-full border-2 border-indigo-500 bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
                                {(user?.displayName?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 py-6">
                {activeTab === 'journal' && (
                    <div className="space-y-6" data-testid="journal-tab">
                        {/* Writing Desk */}
                        {!selectedDate && (
                        <Card className="p-4 backdrop-blur-md bg-white/90 dark:bg-slate-900/90 border-slate-200/50 dark:border-slate-700/50 shadow-xl">
                            <div className="space-y-3">
                                {/* Header Row: Greeting + Language + Date */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 truncate">
                                        Hey {user?.displayName?.split(' ')[0] || 'there'}, {greeting}?
                                    </h2>
                                    
                                    <div className="flex items-center gap-2">
                                        <Input
                                            type="datetime-local"
                                            value={entryDate}
                                            onChange={(e) => setEntryDate(e.target.value)}
                                            data-testid="entry-datetime-picker"
                                            className="w-full sm:w-56 h-7 text-xs bg-transparent border-slate-200 dark:border-slate-700"
                                        />
                                    </div>
                                </div>

                                {/* Text Area */}
                                <div className="relative">
                                    <Textarea
                                        placeholder="Start writing..."
                                        value={newEntry}
                                        onChange={(e) => setNewEntry(e.target.value)}
                                        data-testid="journal-textarea"
                                        className="min-h-[160px] font-serif text-base bg-white/50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 focus:border-indigo-500 dark:focus:border-indigo-400 resize-none pr-10 pb-12"
                                    />
                                    {newEntry.length > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setNewEntry('')}
                                            className="absolute top-2 right-2 p-1.5 h-6 w-6 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
                                            title="Clear text"
                                        >
                                            <X className="w-3 h-3" />
                                        </Button>
                                    )}
                                    {voiceSupported && (
                                        <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
                                            <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
                                                <SelectTrigger
                                                    className="w-[64px] h-8 text-xs px-2 bg-white/80 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700"
                                                    title={`Voice input language: ${VOICE_LANGUAGE_LABELS[selectedLanguage] || selectedLanguage} (does not affect typed text)`}
                                                    data-testid="voice-language-select"
                                                >
                                                    <div className="flex items-center gap-1 min-w-0">
                                                        <Languages className="w-3 h-3 text-indigo-600 dark:text-indigo-400 shrink-0" />
                                                        <span className="text-sm font-medium leading-none">
                                                            {VOICE_LANGUAGE_GLYPHS[selectedLanguage] || 'A'}
                                                        </span>
                                                    </div>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <div className="px-2 py-1.5 text-[11px] text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 mb-1">
                                                        Voice input language
                                                    </div>
                                                    <SelectItem value="en-US">English</SelectItem>
                                                    <SelectItem value="hi-IN">Hindi</SelectItem>
                                                    <SelectItem value="ta-IN">Tamil</SelectItem>
                                                    <SelectItem value="te-IN">Telugu</SelectItem>
                                                    <SelectItem value="ml-IN">Malayalam</SelectItem>
                                                    <SelectItem value="kn-IN">Kannada</SelectItem>
                                                    <SelectItem value="bn-IN">Bengali</SelectItem>
                                                    <SelectItem value="mr-IN">Marathi</SelectItem>
                                                    <SelectItem value="gu-IN">Gujarati</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <Button
                                                onClick={toggleVoiceInput}
                                                disabled={isTranscribing}
                                                data-testid="voice-input-button"
                                                className={`w-8 h-8 p-0 rounded-full transition-all ${
                                                    isListening
                                                        ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                                                        : 'bg-indigo-500 hover:bg-indigo-600'
                                                }`}
                                                title={isListening ? 'Stop voice input' : `Start voice input (${selectedLanguage})`}
                                            >
                                                {isListening ? (
                                                    <MicOff className="w-4 h-4 text-white" />
                                                ) : (
                                                    <Mic className="w-4 h-4 text-white" />
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {isListening && (
                                    <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                                        <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                        <span>Listening...</span>
                                    </div>
                                )}
                                {isTranscribing && (
                                    <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        <span>Transcribing...</span>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="flex gap-2">
                                    <Button
                                        onClick={handleImproveWriting}
                                        disabled={improving || !newEntry.trim()}
                                        data-testid="improve-writing-button"
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 h-9 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-950 text-xs"
                                    >
                                        {improving ? (
                                            <>
                                                <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                                Improving...
                                            </>
                                        ) : (
                                            <>
                                                <Wand2 className="w-3 h-3 mr-2" />
                                                Improve
                                            </>
                                        )}
                                    </Button>
                                    {originalEntry && (
                                        <Button
                                            onClick={handleUndoImprovement}
                                            data-testid="undo-improvement-button"
                                            variant="outline"
                                            size="sm"
                                            className="h-9 w-9 p-0 border-slate-200 dark:border-slate-700"
                                            title="Undo improvement"
                                        >
                                            <Undo2 className="w-3 h-3" />
                                        </Button>
                                    )}
                                    <Button
                                        onClick={handleSaveEntry}
                                        disabled={saving || !newEntry.trim()}
                                        data-testid="save-entry-button"
                                        size="sm"
                                        className="flex-[2] h-9 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium text-xs shadow-md disabled:opacity-50"
                                    >
                                        {saving ? (
                                            <>
                                                <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            'Save Entry'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </Card>
                        )}

                        {/* Timeline */}
                        <div className="space-y-4">
                            {selectedDate && (
                                <div className="flex items-center justify-between">
                                    <p className="text-sm text-slate-600 dark:text-slate-400">
                                        Showing entries for {toLocalDateString(selectedDate)}
                                    </p>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setSelectedDate(null)}
                                        data-testid="clear-date-filter-button"
                                    >
                                        Show All
                                    </Button>
                                </div>
                            )}
                            {filteredEntries.length === 0 ? (
                                <Card className="p-12 backdrop-blur-md bg-white/70 dark:bg-slate-900/70 border-slate-200/50 dark:border-slate-700/50 text-center">
                                    <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                                    <p className="text-slate-600 dark:text-slate-400">
                                        {selectedDate
                                            ? 'No entries for this date'
                                            : 'Your journal is empty. Start writing!'}
                                    </p>
                                </Card>
                            ) : (
                                (() => {
                                    const groupedEntries = groupEntriesByDate(filteredEntries);
                                    return Object.entries(groupedEntries).map(([dateKey, dateEntries]) => (
                                        <div key={dateKey} className="space-y-4">
                                            {/* Date Header */}
                                            <div className="sticky top-16 z-10 backdrop-blur-md bg-gradient-to-r from-indigo-100/90 to-purple-100/90 dark:from-indigo-900/50 dark:to-purple-900/50 px-3 py-2 rounded-lg border border-indigo-200/50 dark:border-indigo-700/50 shadow-sm">
                                                <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200 flex items-center gap-2">
                                                    <Calendar className="w-4 h-4" />
                                                    {dateKey}
                                                    <span className="ml-auto text-xs font-normal text-indigo-700 dark:text-indigo-300">
                                                        {dateEntries.length} {dateEntries.length === 1 ? 'entry' : 'entries'}
                                                    </span>
                                                </h3>
                                            </div>

                                            {/* Entries for this date */}
                                            <div className="space-y-4 pl-2">
                                                {dateEntries.map((entry) => (
                                                    <Card
                                                        key={entry.id}
                                                        data-testid={`journal-entry-${entry.id}`}
                                                        className="p-6 backdrop-blur-md bg-white/90 dark:bg-slate-900/90 border-slate-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all"
                                                    >
                                                        <div className="flex items-start justify-between mb-3">
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-3xl" data-testid="entry-emoji">
                                                                    {entry.ai_metadata?.emoji || entry.emoji || '💭'}
                                                                </span>
                                                                <div>
                                                                    <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400" data-testid="entry-mood">
                                                                        { (entry.ai_status?.analysisSkipped || entry.ai_status?.skipped)
                                                                            ? ( (entry.ai_status?.analysisSkipReason || entry.ai_status?.skipReason) === 'too_large' ? 'Too long for analysis' : 'Empty entry')
                                                                            : (entry.ai_metadata?.primary_emotion || entry.moodLabel || (entry.ai_status?.analyzed === false ? 'Processing...' : '—'))}
                                                                    </p>
                                                                    <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="entry-date">
                                                                        {toLocalTimeString(entry.createdAt)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            {/* Show AI analysis status */}
                                                            <div className="flex items-center gap-2">
                                                                {entry.ai_status?.analyzed === false && !(entry.ai_status?.analysisSkipped || entry.ai_status?.skipped) && !entry.ai_status?.error && (
                                                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                                        Analyzing...
                                                                    </div>
                                                                )}
                                                                {entry.ai_status?.error && !(entry.ai_status?.analysisSkipped || entry.ai_status?.skipped) && (
                                                                    <div className="text-[10px] text-red-500 dark:text-red-400" title={entry.ai_status.error}>
                                                                        Analysis failed
                                                                    </div>
                                                                )}
                                                                {(entry.ai_status?.analysisSkipped || entry.ai_status?.skipped) && (
                                                                    <div 
                                                                        className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-medium cursor-help"
                                                                        title={(entry.ai_status?.analysisSkipReason || entry.ai_status?.skipReason) === 'too_large' 
                                                                            ? 'Entry was too long for AI processing. Keep entries under 8,000 characters for full analysis and insights.' 
                                                                            : 'Entry had no text content to analyze'}
                                                                    >
                                                                        <AlertTriangle className="w-3 h-3" />
                                                                        Skipped
                                                                    </div>
                                                                )}
                                                                <button
                                                                    onClick={() => handleDeleteEntry(entry)}
                                                                    className="p-1.5 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                                                    title="Delete entry"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <p className="font-serif text-slate-700 dark:text-slate-300 mb-3 whitespace-pre-wrap" data-testid="entry-content">
                                                            {entry.text || entry.content}
                                                        </p>

                                                        {/* Helpful note for skipped entries */}
                                                        {(entry.ai_status?.analysisSkipped || entry.ai_status?.skipped) && (
                                                            <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded-lg border border-amber-200/50 dark:border-amber-800/50">
                                                                {(entry.ai_status?.analysisSkipReason || entry.ai_status?.skipReason) === 'too_large' 
                                                                    ? 'AI analysis skipped: entry too long. Split it into shorter entries (under 8,000 characters) to get emotion, summary, and chat memories.'
                                                                    : 'AI analysis skipped: no content provided.'}
                                                            </div>
                                                        )}

                                                        {/* Show friend reaction */}
                                                        {(entry.ai_metadata?.friend_reaction || entry.aiResponse) && (
                                                            <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                                                                <p className="text-sm text-indigo-600 dark:text-indigo-400 italic flex items-start gap-2" data-testid="entry-ai-response">
                                                                    <span>💬</span>
                                                                    <span>{entry.ai_metadata?.friend_reaction || entry.aiResponse}</span>
                                                                </p>
                                                            </div>
                                                        )}
                                                    </Card>
                                                ))}
                                            </div>
                                        </div>
                                    ));
                                })()
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'calendar' && (
                    <div className="space-y-6" data-testid="calendar-tab">
                        <Card className="p-6 backdrop-blur-md bg-white/90 dark:bg-slate-900/90 border-slate-200/50 dark:border-slate-700/50 shadow-xl">
                            {/* Month/Year Header — tap to open picker */}
                            <div className="mb-6 flex items-center justify-between">
                                <Button
                                    variant="ghost"
                                    onClick={() =>
                                        setCurrentMonth(
                                            new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
                                        )
                                    }
                                    data-testid="calendar-prev-month"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </Button>
                                <button
                                    onClick={() => {
                                        setPickerYear(currentMonth.getFullYear());
                                        setShowMonthPicker((prev) => !prev);
                                    }}
                                    className="flex items-center gap-2 text-xl font-semibold text-slate-800 dark:text-slate-100 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors px-3 py-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
                                >
                                    {new Date(currentMonth).toLocaleDateString(undefined, {
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                    <ChevronDown className={`w-4 h-4 transition-transform ${showMonthPicker ? 'rotate-180' : ''}`} />
                                </button>
                                <Button
                                    variant="ghost"
                                    onClick={() =>
                                        setCurrentMonth(
                                            new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
                                        )
                                    }
                                    data-testid="calendar-next-month"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </Button>
                            </div>

                            {/* Month/Year Picker Panel */}
                            {showMonthPicker && (
                                <div className="mb-4 p-4 rounded-xl border border-indigo-200 dark:border-indigo-700/50 bg-indigo-50/50 dark:bg-indigo-950/30 backdrop-blur-sm">
                                    {/* Year selector */}
                                    <div className="flex items-center justify-between mb-4">
                                        <button
                                            onClick={() => setPickerYear((y) => y - 1)}
                                            className="p-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-slate-600 dark:text-slate-300 transition-colors"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </button>
                                        <span className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                                            {pickerYear}
                                        </span>
                                        <button
                                            onClick={() => setPickerYear((y) => y + 1)}
                                            className="p-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-slate-600 dark:text-slate-300 transition-colors"
                                        >
                                            <ChevronRight className="w-5 h-5" />
                                        </button>
                                    </div>
                                    {/* Month grid */}
                                    <div className="grid grid-cols-4 gap-2">
                                        {[
                                            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
                                        ].map((monthLabel, monthIdx) => {
                                            const isActive =
                                                currentMonth.getFullYear() === pickerYear &&
                                                currentMonth.getMonth() === monthIdx;
                                            const isCurrentMonth =
                                                new Date().getFullYear() === pickerYear &&
                                                new Date().getMonth() === monthIdx;
                                            return (
                                                <button
                                                    key={monthLabel}
                                                    onClick={() => {
                                                        setCurrentMonth(new Date(pickerYear, monthIdx));
                                                        setShowMonthPicker(false);
                                                    }}
                                                    className={`py-2 px-1 rounded-lg text-sm font-medium transition-all ${
                                                        isActive
                                                            ? 'bg-indigo-500 text-white shadow-md'
                                                            : isCurrentMonth
                                                            ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/60'
                                                            : 'text-slate-700 dark:text-slate-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40'
                                                    }`}
                                                >
                                                    {monthLabel}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {/* Jump to today */}
                                    <button
                                        onClick={() => {
                                            const now = new Date();
                                            setCurrentMonth(new Date(now.getFullYear(), now.getMonth()));
                                            setPickerYear(now.getFullYear());
                                            setShowMonthPicker(false);
                                        }}
                                        className="mt-3 w-full py-1.5 rounded-lg text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
                                    >
                                        Jump to Today
                                    </button>
                                </div>
                            )}

                            <div className="grid grid-cols-7 gap-2">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                    <div
                                        key={day}
                                        className="text-center text-sm font-medium text-slate-600 dark:text-slate-400 py-2"
                                    >
                                        {day}
                                    </div>
                                ))}

                                {(() => {
                                    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
                                    const days = [];

                                    for (let i = 0; i < startingDayOfWeek; i++) {
                                        days.push(<div key={`empty-${i}`} className="aspect-square" />);
                                    }

                                    for (let day = 1; day <= daysInMonth; day++) {
                                        const date = new Date(
                                            currentMonth.getFullYear(),
                                            currentMonth.getMonth(),
                                            day
                                        );
                                        const dayEntries = getEntriesForDate(date);
                                        const hasEntries = dayEntries.length > 0;

                                        days.push(
                                            <button
                                                key={day}
                                                onClick={() => {
                                                    setSelectedDate(date);
                                                    setActiveTab('journal');
                                                }}
                                                data-testid={`calendar-day-${day}`}
                                                className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-all ${hasEntries
                                                    ? 'bg-indigo-100 dark:bg-indigo-900/30 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 text-indigo-900 dark:text-indigo-200'
                                                    : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                                                    }`}
                                            >
                                                <span className="font-medium">{day}</span>
                                                {hasEntries && (
                                                    <div className="flex gap-0.5 mt-1">
                                                        {dayEntries.slice(0, 3).map((entry, idx) => (
                                                            <div
                                                                key={idx}
                                                                className="w-1 h-1 rounded-full bg-indigo-500"
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    }

                                    return days;
                                })()}
                            </div>
                        </Card>
                    </div>
                )}

                {activeTab === 'chat' && (
                    <div className="space-y-4" data-testid="chat-tab">
                        <Card className="backdrop-blur-md bg-white/90 dark:bg-slate-900/90 border-slate-200/50 dark:border-slate-700/50 shadow-xl">
                            <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-indigo-500" />
                                    Chat with {displayAssistantName}
                                </h2>
                                <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                                    Your digital best friend - ask about patterns, feelings, or memories
                                </p>
                            </div>

                            <div className="h-[65vh] min-h-[400px] overflow-y-auto p-4 space-y-4">
                                {chatMessages.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-center">
                                        <MessageCircle className="w-12 h-12 text-slate-400 mb-4" />
                                        <p className="text-slate-600 dark:text-slate-400">
                                            Start a conversation with {displayAssistantName}
                                        </p>
                                        <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">
                                            Try: "What patterns do you see in my entries?" or "How have I been feeling lately?"
                                        </p>
                                    </div>
                                ) : (
                                    chatMessages.map((msg, idx) => (
                                        <div
                                            key={idx}
                                            data-testid={`chat-message-${idx}`}
                                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'
                                                }`}
                                        >
                                            <div
                                                className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.role === 'user'
                                                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200'
                                                    }`}
                                            >
                                                <p className="whitespace-pre-wrap">{msg.content}</p>
                                            </div>
                                        </div>
                                    ))
                                )}
                                {chatLoading && (
                                    <div className="flex justify-start">
                                        <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl px-4 py-3 flex items-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
                                            <span className="text-sm text-slate-600 dark:text-slate-400">{displayAssistantName} is thinking...</span>
                                        </div>
                                    </div>
                                )}
                                <div ref={chatEndRef} />
                            </div>

                            <div className="p-3 border-t border-slate-200 dark:border-slate-700">
                                <div className="flex gap-2">
                                    <Input
                                        placeholder={`Ask ${displayAssistantName} anything...`}
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleChatSend()}
                                        data-testid="chat-input"
                                        disabled={chatLoading}
                                        className="flex-1"
                                    />
                                    <Button
                                        onClick={handleChatSend}
                                        disabled={chatLoading || !chatInput.trim()}
                                        data-testid="chat-send-button"
                                        className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                                    >
                                        <Send className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className="space-y-8 max-w-3xl mx-auto" data-testid="settings-tab">
                        <div>
                            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                                Settings
                            </h2>
                            <p className="text-slate-500 dark:text-slate-400">
                                Manage your preferences and account details
                            </p>
                        </div>

                        <div className="space-y-6">
                            {/* Appearance Section */}
                            <section className="bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg text-indigo-600 dark:text-indigo-400">
                                        <Palette className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Appearance</h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Customize how Diary AI looks on your device</p>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-3 gap-3">
                                    <Button
                                        variant={theme === 'light' ? 'default' : 'outline'}
                                        onClick={() => handleThemeChange('light')}
                                        data-testid="theme-light-button"
                                        className={`h-auto py-4 flex flex-col gap-2 ${theme === 'light' ? 'bg-indigo-600 hover:bg-indigo-700' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                    >
                                        <Sun className="w-5 h-5" />
                                        <span className="font-medium">Light</span>
                                    </Button>
                                    <Button
                                        variant={theme === 'dark' ? 'default' : 'outline'}
                                        onClick={() => handleThemeChange('dark')}
                                        data-testid="theme-dark-button"
                                        className={`h-auto py-4 flex flex-col gap-2 ${theme === 'dark' ? 'bg-indigo-600 hover:bg-indigo-700' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                    >
                                        <Moon className="w-5 h-5" />
                                        <span className="font-medium">Dark</span>
                                    </Button>
                                    <Button
                                        variant={theme === 'system' ? 'default' : 'outline'}
                                        onClick={() => handleThemeChange('system')}
                                        data-testid="theme-system-button"
                                        className={`h-auto py-4 flex flex-col gap-2 ${theme === 'system' ? 'bg-indigo-600 hover:bg-indigo-700' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                    >
                                        <Monitor className="w-5 h-5" />
                                        <span className="font-medium">System</span>
                                    </Button>
                                </div>
                            </section>

                            {/* Notifications Section */}
                            <section className="bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm hidden">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg text-amber-600 dark:text-amber-400">
                                        <Bell className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Notifications</h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Manage your daily reminders</p>
                                    </div>
                                </div>
                                
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 flex items-center justify-between gap-4">
                                    <div className="space-y-1">
                                        <p className="font-medium text-slate-900 dark:text-slate-100">Push Notifications</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Receive daily reminders to journal</p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        onClick={handleRequestNotifications}
                                        data-testid="enable-notifications-button"
                                        className="shrink-0"
                                    >
                                        Enable
                                    </Button>
                                </div>
                            </section>

                            {/* AI Companion Section */}
                            <section className="bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg text-pink-600 dark:text-pink-400">
                                        <Bot className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">AI Companion</h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Customize your personal AI assistant with the name you like</p>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <Input
                                        value={assistantNameDraft}
                                        onChange={(e) => setAssistantNameDraft(e.target.value)}
                                        data-testid="assistant-name-input"
                                        className="flex-1"
                                        placeholder="Assistant Name"
                                    />
                                    <Button
                                        onClick={handleAssistantNameSave}
                                        disabled={!assistantNameDraft.trim() || assistantNameDraft.trim() === assistantName}
                                        data-testid="assistant-name-save-button"
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                    >
                                        Save Changes
                                    </Button>
                                </div>
                            </section>

                            {/* Data & Account Section */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <section className="bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm hidden">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg text-emerald-600 dark:text-emerald-400">
                                            <FileJson className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Data Export</h3>
                                        </div>
                                    </div>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Download a copy of all your journal entries in JSON format.</p>
                                    <Button
                                        variant="outline"
                                        onClick={handleExportData}
                                        data-testid="export-data-button"
                                        className="w-full justify-start"
                                    >
                                        <Download className="w-4 h-4 mr-2" />
                                        Export Data
                                    </Button>
                                </section>

                                <section className="md:col-span-2 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-sm">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-400">
                                            <User className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Account</h3>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-5 mb-6">
                                        {avatarUrl && !avatarError ? (
                                            <img
                                                src={avatarUrl}
                                                alt={user?.displayName || 'User'}
                                                onError={() => setAvatarError(true)}
                                                className="w-20 h-20 rounded-full border-2 border-slate-200 dark:border-slate-700"
                                            />
                                        ) : (
                                            <div className="w-20 h-20 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-2xl font-bold border-2 border-slate-200 dark:border-slate-700">
                                                {(user?.displayName?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                                            </div>
                                        )}
                                        <div className="overflow-hidden">
                                            <p className="text-xl font-semibold text-slate-900 dark:text-slate-100 truncate">
                                                {user?.displayName || 'User'}
                                            </p>
                                            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                                                {user?.email}
                                            </p>
                                        </div>
                                    </div>

                                    <Button
                                        variant="ghost"
                                        onClick={handleSignOut}
                                        data-testid="sign-out-button"
                                        className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Sign Out
                                    </Button>
                                </section>
                            </div>
                            
                            <div className="text-center">
                                <p className="text-xs text-slate-400 dark:text-slate-600 font-medium">
                                    Diary AI v1.0 • Built by Karthik Arwin
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            {/* Bottom Navigation */}
            <nav className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-md bg-white/90 dark:bg-slate-900/90 border-t border-slate-200/50 dark:border-slate-700/50 shadow-2xl">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-around">
                    <button
                        onClick={() => setActiveTab('journal')}
                        data-testid="nav-journal-button"
                        className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all ${activeTab === 'journal'
                            ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                            }`}
                    >
                        <BookOpen className="w-5 h-5" />
                        <span className="text-xs font-medium">Journal</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('calendar')}
                        data-testid="nav-calendar-button"
                        className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all ${activeTab === 'calendar'
                            ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                            }`}
                    >
                        <Calendar className="w-5 h-5" />
                        <span className="text-xs font-medium">Calendar</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('chat')}
                        data-testid="nav-chat-button"
                        className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all ${activeTab === 'chat'
                            ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                            }`}
                    >
                        <MessageCircle className="w-5 h-5" />
                        <span className="text-xs font-medium">Chat</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('settings')}
                        data-testid="nav-settings-button"
                        className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all ${activeTab === 'settings'
                            ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950'
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                            }`}
                    >
                        <Settings className="w-5 h-5" />
                        <span className="text-xs font-medium">Settings</span>
                    </button>
                </div>
            </nav>

            <AlertDialog open={!!entryToDelete} onOpenChange={(open) => !open && setEntryToDelete(null)}>
                <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-slate-900 dark:text-slate-100">
                            Delete Entry?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-600 dark:text-slate-400">
                            This will permanently delete this journal entry. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel 
                            className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 h-8 px-3 text-xs"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-red-600 hover:bg-red-700 text-white border-red-600 h-8 px-3 text-xs"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default Dashboard;
